import React, { useMemo, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
} from "@mui/material";
import SnackBar from "../../../components/SnackBar";
import RelatorioContent from "./RelatorioContent";
import { processarDadosResumos, calcularTotais } from "./utils";
import { ContainerRenderToPDF } from "../../../components/ContainerRenderPDF";
import html2pdf from "html2pdf.js";
import {
  enviarPreviaIcms,
} from "../../../services/api-planejamento-tributario.service";

const convertBlobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      const base64data = result.split(",")[1];
      resolve(base64data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const GerarRelatorioResumo = ({
  resumos = [],
  dadosRelatorioEstruturado = {},
  labels = [],
  onClose,
  dadosContexto = {},
}) => {
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);
  const pdfRef = useRef();

  // Determinar tipo de prévia
  const tipoPrevia = dadosContexto?.tipoPrevia || "PIS/COFINS";

  // Dados baseados no estado estruturado (unificado para PIS/COFINS e ICMS)
  const mockData = useMemo(() => {
    console.log("🔍 DEBUG GerarRelatorioResumo - dados recebidos:", {
      resumos,
      dadosRelatorioEstruturado,
      dadosContexto,
      labels,
    });

    const steps = [];
    const chaves = Object.keys(dadosRelatorioEstruturado);

    // Processar cada resumo diretamente, não pelas chaves
    if (resumos && resumos.length > 0) {
      resumos.forEach((resumo, index) => {
        console.log(`🔍 DEBUG processando resumo ${index}:`, resumo);

        // Buscar dados do tipoPrevia na categoria
        let nomeMotor = `Motor ${resumo.idTipoPrevia}`;
        let fundamentacao = "";
        let medidaJudicial = "DESNECESSIDADE";

        // Tentar diferentes estruturas para encontrar os dados
        if (dadosContexto?.categoria?.tiposPrevia) {
          const tipoPrevia = dadosContexto.categoria.tiposPrevia.find(
            (tp) => tp.idTipoPrevia === resumo.idTipoPrevia
          );
          if (tipoPrevia) {
            nomeMotor = tipoPrevia.descricao || nomeMotor;
            fundamentacao = tipoPrevia.fundamentacao || "";
            medidaJudicial = tipoPrevia.medidaJudicial || "DESNECESSIDADE";
          }
        }

        // Tentar buscar dados diretamente do resumo
        if (resumo?.resumo?.complemento) {
          nomeMotor = resumo.resumo.complemento.descricao || nomeMotor;
          fundamentacao =
            resumo.resumo.complemento.fundamentacao || fundamentacao;
          medidaJudicial =
            resumo.resumo.complemento.medidaJudicial || medidaJudicial;
        }

        // Tentar buscar dados do complemento direto
        if (resumo?.complemento) {
          nomeMotor = resumo.complemento.descricao || nomeMotor;
          fundamentacao = resumo.complemento.fundamentacao || fundamentacao;
          medidaJudicial = resumo.complemento.medidaJudicial || medidaJudicial;
        }

        console.log(`📊 DEBUG resumo ${index} processado:`, {
          nomeMotor,
          fundamentacao,
          medidaJudicial,
        });

        // Calcular total baseado nos dados do resumo (composicaoProjeto = true)
        let total = 0;
        if (resumo?.dados && Array.isArray(resumo.dados)) {
          for (const row of resumo.dados) {
            // Verificar se Composição Projeto__boolean é verdadeiro
            const composicaoProjeto = row["Composição Projeto__boolean"];

            if (composicaoProjeto === true) {
              // Pegar o valor de Composição Calculo__monetary
              const composicaoCalculo = row["Composição Calculo__monetary"];

              const num = Number(composicaoCalculo);
              if (!Number.isNaN(num)) {
                total += num;
              }
            }
          }
        }

        steps.push({
          step: nomeMotor,
          teseJudicial: fundamentacao,
          valor: total,
          composicaoProjeto: total > 0,
          tipo: medidaJudicial,
        });
      });
    }

    return steps;
  }, [dadosRelatorioEstruturado, resumos, dadosContexto]);

  // Calcular totais (unificado para PIS/COFINS e ICMS)
  const totais = useMemo(() => {
    return calcularTotais(mockData);
  }, [mockData]);

  // Função para enviar prévia seguindo o padrão do INSS
  const handleEnviarPrevia = async () => {
    setLoading(true);

    if (!pdfRef.current?.getElement) {
      setLoading(false);
      setObjSnackBar({
        type: "error",
        msg: "Elemento DOM do PDF não encontrado.",
      });
      return;
    }

    if (!dadosContexto?.idPreviaVersao) {
      setLoading(false);
      setObjSnackBar({
        type: "error",
        msg: "ID da versão da prévia não encontrado.",
      });
      return;
    }

    try {
      const element = pdfRef.current.getElement();

      const blob = await html2pdf()
        .from(element)
        .set({
          margin: [20, 20, 20, 20],
          jsPDF: { unit: "px", format: [1920, 1080], orientation: "landscape" },
          html2canvas: { scale: 2, useCORS: true },
        })
        .outputPdf("blob");

      const base64 = await convertBlobToBase64(blob);

      const payload = {
        idPreviaVersao: dadosContexto.idPreviaVersao,
        pdfBase64: base64,
        // Email removido - API busca automaticamente através do CNPJ
      };

      let response;
      // Escolher função baseada no tipo de prévia
      if (tipoPrevia === "ICMS") {
        response = await enviarPreviaIcms(payload);
      } else {
        setLoading(false);
        setObjSnackBar({
          type: "warning",
          msg: "Envio de prévia PIS/COFINS temporariamente indisponível.",
        });
        return;
      }
      // Debug: log da resposta completa
      console.log("🔍 Resposta completa da API:", response);
      console.log("🔍 response.data:", response?.data);
      console.log("🔍 response.data.success:", response?.data?.success);
      console.log(
        "🔍 typeof response.data.success:",
        typeof response?.data?.success
      );
      console.log("🔍 response.status:", response?.status);
      console.log("🔍 response.statusText:", response?.statusText);

      setLoading(false);

      // Tratar resposta de sucesso da API
      if (
        (response?.data?.success === true ||
          response?.data?.success === "true") &&
        response?.status === 200
      ) {
        let successMessage =
          tipoPrevia === "ICMS"
            ? response.data.message || "Prévia ICMS enviada com sucesso!"
            : response.data.message || "Prévia PIS/COFINS enviada com sucesso!";

        // Adicionar warning se existir
        if (response.data.warning) {
          successMessage += ` ${response.data.warning}`;
        }

        setObjSnackBar({
          type: "success",
          msg: successMessage,
        });
        onClose?.();
      } else {
        // Resposta inesperada - log para debug
        console.error("❌ Resposta inesperada da API:", response?.data);
        console.error("❌ Status:", response?.status);
        console.error("❌ Success value:", response?.data?.success);
        console.error("❌ Success type:", typeof response?.data?.success);

        setObjSnackBar({
          type: "error",
          msg: "Resposta inesperada da API. Tente novamente.",
        });
      }
    } catch (error) {
      setLoading(false);

      // Tratar diferentes tipos de erro da API
      let errorMessage =
        tipoPrevia === "ICMS"
          ? "Erro ao enviar prévia ICMS."
          : "Erro ao enviar prévia PIS/COFINS.";

      if (error?.response?.data) {
        const apiError = error.response.data;

        // Erro estruturado da API (sucesso: false, mensagem: string)
        if (apiError.sucesso === false && apiError.mensagem) {
          errorMessage = apiError.mensagem;
        }
        // Erro de validação (error: string)
        else if (apiError.error) {
          errorMessage = apiError.error;
        }
        // Erro genérico com mensagem
        else if (apiError.message) {
          errorMessage = apiError.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setObjSnackBar({
        type: "error",
        msg: errorMessage,
      });
    }
  };

  // Determinar título e componente baseado no tipo
  const titulo =
    tipoPrevia === "ICMS"
      ? "Prévia Consolidada ICMS"
      : "Prévia Consolidada PIS/COFINS";
  const filename =
    tipoPrevia === "ICMS"
      ? "relatorio-previa-icms.pdf"
      : "relatorio-previa-piscofins.pdf";
  // Usar componente unificado para ambos os tipos
  const RelatorioComponent = RelatorioContent;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{titulo}</DialogTitle>
      <DialogContent dividers style={{ backgroundColor: "#fff" }}>
        <ContainerRenderToPDF
          ref={pdfRef}
          filename={filename}
          orientation="portrait"
          sx={{ padding: "20px", backgroundColor: "#fff" }}
        >
          <RelatorioComponent mockData={mockData} totais={totais} />
        </ContainerRenderToPDF>
        <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        {tipoPrevia === "ICMS" && (
          <Button
            variant="contained"
            onClick={handleEnviarPrevia}
            disabled={loading}
          >
            {loading ? "ENVIANDO..." : "ENVIAR"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GerarRelatorioResumo;
